import { useEffect, useState } from "react";
import { remoteConfig } from "utils/firebase";
import {
  activate,
  fetchAndActivate,
  getAll,
  onConfigUpdate,
} from "firebase/remote-config";

// ---- coercion whitelists ----------------------------------------------
const BOOLEAN_KEYS = new Set([ "is_drive_maintenance"]);
const NUMBER_KEYS = new Set(["drive_max_file_size", "drive_select_file_limit"]);

const parseConfig = (allConfig) => {
  const parsed = {};
  Object.entries(allConfig).forEach(([key, val]) => {
    const s = val.asString();
    if (BOOLEAN_KEYS.has(key)) {
      parsed[key] = s === "true";
    } else if (NUMBER_KEYS.has(key)) {
      const n = Number(s);
      parsed[key] = Number.isFinite(n) ? n : 0;
    } else {
      parsed[key] = s; // strings stay strings (country, color, default_country, etc.)
    }
  });
  return parsed;
};

// ---- module-level singletons -------------------------------------------
let cachedConfig = null;
let listenerAttached = false;
let initialFetchDone = false;
const subscribers = new Set();

const notify = (cfg) => {
  cachedConfig = cfg;
  subscribers.forEach((cb) => cb(cfg));
};

const attachListener = () => {
  if (listenerAttached) return;
  listenerAttached = true;

  onConfigUpdate(remoteConfig, {
    next: async () => {
      try {
        await activate(remoteConfig); // new config already downloaded
        notify(parseConfig(getAll(remoteConfig)));
      } catch (err) {
        console.error("Failed to activate real-time config:", err);
      }
    },
    error: (err) => console.error("Remote Config listener error:", err),
    complete: () => console.log("Remote Config listener complete"),
  });
};

// ---- hook --------------------------------------------------------------
const useRemoteConfig = () => {
  const [config, setConfig] = useState(cachedConfig || {});
  const [loading, setLoading] = useState(!cachedConfig);

  useEffect(() => {
    let cancelled = false;
    const subscriber = (cfg) => {
      if (!cancelled) setConfig(cfg);
    };
    subscribers.add(subscriber);

    (async () => {
      try {
        if (!initialFetchDone) {
          await fetchAndActivate(remoteConfig);
          initialFetchDone = true;
          notify(parseConfig(getAll(remoteConfig)));
        }
        attachListener();
      } catch (err) {
        console.error("Remote Config initial fetch failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      subscribers.delete(subscriber);
    };
  }, []);

  return { config, loading };
};

export default useRemoteConfig;
